import React from 'react'

// material ui
import Box from '@mui/material/Box'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'

// components
import FormControlLabel from '@mui/material/FormControlLabel'
import DayPassProduct from './DayPassProduct'
import VolumeDiscountProduct from './VolumeDiscountProduct'
import SummerPassProduct from './SummerPassProduct'

// helpers
import {
  volumeDiscountApplied,
  volumeDiscountDayPrice
} from 'lib/cart/decorators/cartProduct'

const ProductMenu = props => {
  const {
    camper,
    session,
    plan,
    product,
    location,
    cartProduct,
    camperSession,
    onChangeProduct,
    account
  } = props

  // for integration tests
  const testId = product =>
    `camper-${camper.id}-session-${session.id}-product-${product.id}-radio`

  const { selectedType } = cartProduct
  const { upgradeCreditableDays } = camperSession

  // Create an enhanced cartProduct with accountDiscount from FAA if it doesn't exist yet
  // Apply FA if available, regardless of existing plans - show current prices
  let enhancedCartProduct = cartProduct
  if (!cartProduct.accountDiscount && account && account.financialAidApprovals && session) {
    // Only apply financial aid if it's available for this session
    if (session.financialAidAvailable) {
      const activeApproval = account.financialAidApprovals.find(
        approval => approval.year === session.year && approval.status === 'active'
      )
      if (activeApproval) {
        enhancedCartProduct = {
          ...cartProduct,
          accountDiscount: {
            discountCents: activeApproval.discountCents,
            discountCategory: 'Financial Aid'
          }
        }
      }
    }
  }

  // day pass pricing
  const accountDiscountCents = enhancedCartProduct.accountDiscount?.discountCents || 0
  const dayPassPriceCents = product.variablePriceCents - accountDiscountCents

  const hasVolumeDiscount = volumeDiscountApplied(enhancedCartProduct)
  const volumeDiscountPriceCents = volumeDiscountDayPrice(enhancedCartProduct, account)

  // can't change product if there is only one choice
  if (!product.breakEvenDays && !product.volumeDiscountDays) {
    return (
      <Box my={1}>
        <DayPassProduct
          camper={camper}
          session={session}
          cartProduct={enhancedCartProduct}
          camperSession={camperSession}
          product={product}
          plan={plan}
          dayPassPriceCents={dayPassPriceCents}
          volumeDiscountPriceCents={volumeDiscountPriceCents}
          account={account}
        />
      </Box>
    )
  }

  return (
    <RadioGroup
      aria-label='product'
      name='product'
      value={selectedType}
      onChange={onChangeProduct}
    >
      <FormControlLabel
        value='day-pass'
        control={<Radio disabled={hasVolumeDiscount} />}
        data-test-id={testId(product)}
        label={
          <DayPassProduct
            camper={camper}
            session={session}
            cartProduct={enhancedCartProduct}
            camperSession={camperSession}
            product={product}
            plan={plan}
            dayPassPriceCents={dayPassPriceCents}
            volumeDiscountPriceCents={volumeDiscountPriceCents}
            account={account}
          />
        }
      />

      {product.volumeDiscountCents > 0 && (
        <FormControlLabel
          value='volume-discount'
          control={<Radio />}
          data-test-id={testId(product)}
          label={
            <VolumeDiscountProduct
              camper={camper}
              session={session}
              location={location}
              cartProduct={enhancedCartProduct}
              camperSession={camperSession}
              product={product}
              plan={plan}
              dayPassPriceCents={dayPassPriceCents}
              volumeDiscountPriceCents={volumeDiscountPriceCents}
              account={account}
            />
          }
        />
      )}

      {product.breakEvenDays > 0 && (
        <FormControlLabel
          value='unlimited'
          control={<Radio />}
          data-test-id={testId(product)}
          label={
            <SummerPassProduct
              camper={camper}
              session={session}
              cartProduct={enhancedCartProduct}
              camperSession={camperSession}
              product={product}
              plan={plan}
              volumeDiscountPriceCents={volumeDiscountPriceCents}
              account={account}
            />
          }
        />
      )}
    </RadioGroup>
  )
}

export default ProductMenu
